import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { format } from 'date-fns';
import {
  Truck,
  Package,
  MapPin,
  CalendarBlank,
  Spinner,
  CheckCircle,
  Clock,
  Eye,
  UserPlus
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const CURRENCY_SYMBOLS = { USD: '$', XCD: 'EC$', JMD: 'J$', TTD: 'TT$' };

const SHIPMENT_STATUS_LABELS = {
  pending_assignment: 'Pending Assignment',
  assigned: 'Assigned',
  picked_up: 'Picked Up',
  in_transit: 'In Transit',
  arrived: 'Arrived'
};

const SHIPMENT_STATUS_COLORS = {
  pending_assignment: 'bg-[#F4A261]/10 text-[#B45309]',
  assigned: 'bg-[#0F52BA]/10 text-[#0F52BA]',
  picked_up: 'bg-[#38B2AC]/10 text-[#0F766E]',
  in_transit: 'bg-[#2D6A4F]/10 text-[#2D6A4F]',
  arrived: 'bg-slate-200 text-slate-700'
};

const ShipmentsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [freightForwarders, setFreightForwarders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Assignment form
  const [selectedFF, setSelectedFF] = useState('');
  const [cargoReadyDate, setCargoReadyDate] = useState(new Date());
  const [assignmentNotes, setAssignmentNotes] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [shipmentsRes, ffRes] = await Promise.all([
        axios.get(`${API_URL}/api/shipments/ready-for-shipping`, getAuthHeaders()),
        axios.get(`${API_URL}/api/freight-forwarders`, getAuthHeaders())
      ]);
      setShipments(shipmentsRes.data);
      setFreightForwarders(ffRes.data);
    } catch (error) {
      toast.error('Failed to load shipments data');
    } finally {
      setLoading(false);
    }
  };

  const openAssignDialog = (po) => {
    setSelectedPO(po);
    setSelectedFF(po.assigned_freight_forwarder_id || '');
    setCargoReadyDate(po.cargo_ready_date ? new Date(po.cargo_ready_date) : new Date());
    setAssignmentNotes('');
    setAssignDialogOpen(true);
  };

  const handleAssign = async () => {
    if (!selectedPO || !selectedFF) return;
    setSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/purchase-orders/${selectedPO.id}/assign-freight-forwarder`,
        {
          freight_forwarder_id: selectedFF,
          cargo_ready_date: cargoReadyDate.toISOString(),
          notes: assignmentNotes
        },
        getAuthHeaders()
      );
      toast.success('Freight forwarder assigned successfully');
      setAssignDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Assignment failed');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const color = SHIPMENT_STATUS_COLORS[status] || 'bg-slate-100 text-slate-600';
    const label = SHIPMENT_STATUS_LABELS[status] || status;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        {label}
      </span>
    );
  };

  // Separate into ready for assignment and in progress
  const pendingAssignment = shipments.filter(s => s.shipment_status === 'pending_assignment');
  const assigned = shipments.filter(s => s.shipment_status === 'assigned');
  const inProgress = shipments.filter(s => ['in_transit', 'picked_up'].includes(s.shipment_status));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0F52BA] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Shipments
        </h1>
        <p className="text-slate-500 mt-1">Manage freight forwarder assignments and track shipments</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#F4A261]/10 flex items-center justify-center">
              <Clock size={20} weight="duotone" className="text-[#F4A261]" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Pending Assignment</p>
              <p className="text-xl font-bold text-slate-800">{pendingAssignment.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#0F52BA]/10 flex items-center justify-center">
              <UserPlus size={20} weight="duotone" className="text-[#0F52BA]" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Assigned</p>
              <p className="text-xl font-bold text-slate-800">{assigned.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#2D6A4F]/10 flex items-center justify-center">
              <Truck size={20} weight="duotone" className="text-[#2D6A4F]" />
            </div>
            <div>
              <p className="text-sm text-slate-500">In Transit</p>
              <p className="text-xl font-bold text-slate-800">{inProgress.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#38B2AC]/10 flex items-center justify-center">
              <Package size={20} weight="duotone" className="text-[#38B2AC]" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Shipments</p>
              <p className="text-xl font-bold text-slate-800">{shipments.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Assignment Section */}
      {pendingAssignment.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 bg-[#F4A261]/5">
            <h2 className="font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Ready for Freight Forwarder Assignment
            </h2>
            <p className="text-sm text-slate-500">POs confirmed by vendors and ready to ship</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">PO Number</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Vendor</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Origin</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Value</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Delivery Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pendingAssignment.map(po => (
                  <tr key={po.id} className="hover:bg-slate-50/50" data-testid={`shipment-row-${po.po_number}`}>
                    <td className="px-4 py-4">
                      <p className="font-medium text-slate-800">{po.po_number}</p>
                    </td>
                    <td className="px-4 py-4 text-slate-600">{po.vendor_name}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2 text-slate-600">
                        <MapPin size={16} className="text-slate-400" />
                        {po.origin_country}
                      </div>
                    </td>
                    <td className="px-4 py-4 font-medium text-slate-800">
                      {CURRENCY_SYMBOLS[po.currency]}{po.total_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-4 text-slate-600">
                      {new Date(po.required_delivery_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4">
                      <Button
                        size="sm"
                        onClick={() => openAssignDialog(po)}
                        className="bg-[#0F52BA] hover:bg-[#0A3D8F]"
                        data-testid={`assign-ff-${po.po_number}`}
                      >
                        <UserPlus size={16} className="mr-2" />
                        Assign Freight Forwarder
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Assigned & In Transit Section */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            All Shipments
          </h2>
        </div>
        {shipments.length === 0 ? (
          <div className="text-center py-16">
            <Truck size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No shipments to display</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">PO Number</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Vendor</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Origin</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Cargo Ready</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Freight Forwarder</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {shipments.map(po => (
                  <tr key={po.id} className="hover:bg-slate-50/50" data-testid={`all-shipment-${po.po_number}`}>
                    <td className="px-4 py-4">
                      <p className="font-medium text-slate-800">{po.po_number}</p>
                    </td>
                    <td className="px-4 py-4 text-slate-600">{po.vendor_name}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2 text-slate-600">
                        <MapPin size={16} className="text-slate-400" />
                        {po.origin_country}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-slate-600">
                      {po.cargo_ready_date ? new Date(po.cargo_ready_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-4">
                      {po.assigned_freight_forwarder_name ? (
                        <span className="text-slate-800">{po.assigned_freight_forwarder_name}</span>
                      ) : (
                        <span className="text-slate-400 italic">Not assigned</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {getStatusBadge(po.shipment_status)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-2">
                        {!po.assigned_freight_forwarder_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAssignDialog(po)}
                            data-testid={`assign-btn-${po.po_number}`}
                          >
                            <UserPlus size={16} />
                          </Button>
                        )}
                        {po.shipment && (
                          <Button size="sm" variant="ghost">
                            <Eye size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Assign Freight Forwarder Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
              Assign Freight Forwarder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedPO && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-800">{selectedPO.po_number}</p>
                <p className="text-sm text-slate-500">
                  {selectedPO.vendor_name} • {selectedPO.origin_country}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Select Freight Forwarder</Label>
              <Select value={selectedFF} onValueChange={setSelectedFF}>
                <SelectTrigger data-testid="ff-select">
                  <SelectValue placeholder="Choose a freight forwarder" />
                </SelectTrigger>
                <SelectContent>
                  {freightForwarders.map(ff => (
                    <SelectItem key={ff.id} value={ff.id}>
                      <div>
                        <span className="font-medium">{ff.company_name}</span>
                        <span className="text-slate-500 ml-2 text-sm">
                          ({ff.territories_served.slice(0, 2).join(', ')})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Show selected FF details */}
            {selectedFF && (
              <div className="p-3 bg-[#38B2AC]/5 rounded-lg border border-[#38B2AC]/20">
                {(() => {
                  const ff = freightForwarders.find(f => f.id === selectedFF);
                  if (!ff) return null;
                  return (
                    <div className="space-y-1 text-sm">
                      <p className="font-medium text-slate-800">{ff.company_name}</p>
                      <p className="text-slate-600">{ff.contact_person} • {ff.phone}</p>
                      <p className="text-slate-500">{ff.email}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {ff.ports_served.map(port => (
                          <span key={port} className="px-2 py-0.5 bg-white rounded text-xs text-slate-600">
                            {port}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <div className="space-y-2">
              <Label>Estimated Cargo Ready Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left" data-testid="cargo-ready-date">
                    <CalendarBlank size={18} className="mr-2" />
                    {format(cargoReadyDate, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={cargoReadyDate}
                    onSelect={(date) => setCargoReadyDate(date || new Date())}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
                placeholder="Special handling instructions, pickup details..."
                data-testid="assignment-notes"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button variant="secondary" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleAssign}
                disabled={submitting || !selectedFF}
                data-testid="confirm-assign-btn"
              >
                {submitting ? <Spinner size={20} className="animate-spin mr-2" /> : <CheckCircle size={20} className="mr-2" />}
                Assign & Notify
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShipmentsPage;
